import React from 'react';
import { motion } from 'framer-motion';
import { 
  ZoomIn, 
  Moon, 
  Clock,
  ArrowUpDown,
  Palette,
  Shield
} from 'lucide-react';

const features = [
  {
    icon: <ArrowUpDown className="w-6 h-6" />,
    title: "Flexible Viewing Options",
    description: "Choose between vertical scrolling (traditional) or horizontal scrolling (magazine-style) reading modes for your preferred experience.",
    color: "from-blue-500 to-blue-600"
  },
  {
    icon: <ZoomIn className="w-6 h-6" />,
    title: "Intelligent Zoom Controls",
    description: "Smooth zoom in/out with reset functionality, optimized for both touch gestures and precise control.",
    color: "from-green-500 to-green-600"
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: "Smart File Management",
    description: "Recently accessed files are automatically saved and organized for quick access to your most frequently viewed documents.",
    color: "from-purple-500 to-purple-600"
  },
  {
    icon: <Palette className="w-6 h-6" />,
    title: "Theme Flexibility",
    description: "Light mode, dark mode, or automatic system theme following for comfortable reading in any environment.",
    color: "from-orange-500 to-orange-600"
  },
  {
    icon: <img src={process.env.PUBLIC_URL + '/favicon.png'} alt="PDF" className="w-6 h-6 rounded" />,
    title: "Universal Compatibility",
    description: "Support for standard PDF files from any source with seamless system integration.",
    color: "from-red-500 to-red-600"
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Edge-to-Edge Design",
    description: "Immersive reading experience with modern Android styling and Material Design 3 aesthetics.",
    color: "from-indigo-500 to-indigo-600"
  }
];

const Features = () => {
  return (
    <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
            Key Capabilities
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Experience the next generation of PDF viewing with our advanced document reader. 
            Built with cutting-edge technology and user-centric design principles for seamless document handling.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card p-6 group"
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                <div className="text-white">
                  {feature.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-secondary-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Additional Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-20 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 md:p-12"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
                Advanced User Experience
              </h3>
              <p className="text-secondary-600 mb-6 leading-relaxed">
                Immerse yourself in a sophisticated reading environment designed for maximum productivity. 
                Our intelligent interface adapts to your workflow, providing seamless navigation and enhanced document interaction.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                  <span className="text-secondary-700">Material Design 3 aesthetics</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                  <span className="text-secondary-700">Immersive reading experience</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                  <span className="text-secondary-700">Seamless file picker integration</span>
                </div>
              </div>
            </div>
                         <div className="relative">
               <div className="bg-white rounded-xl p-6 shadow-lg">
                 <div className="flex items-center justify-between mb-4">
                   <div className="flex items-center space-x-3">
                     <img 
                       src={process.env.PUBLIC_URL + '/favicon.png'} 
                       alt="PDF Viewer" 
                       className="w-8 h-8 rounded-lg"
                       onError={(e) => {
                         console.log('Image failed to load:', e.target.src);
                         e.target.style.display = 'none';
                       }}
                     />
                     <span className="font-semibold text-secondary-900">PDF Viewer</span>
                   </div>
                   <div className="flex items-center space-x-2">
                     <Moon className="w-4 h-4 text-secondary-500" />
                     <div className="w-8 h-2 bg-secondary-200 rounded-full">
                       <div className="w-4 h-2 bg-primary-600 rounded-full"></div>
                     </div>
                   </div>
                 </div>
                 <div className="space-y-3">
                   <div className="h-2 bg-secondary-200 rounded w-full"></div>
                   <div className="h-2 bg-secondary-200 rounded w-3/4"></div>
                   <div className="h-2 bg-secondary-200 rounded w-1/2"></div>
                 </div>
               </div>
             </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features; 